import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { getCommitteeAssignments } from 'apis/repositories/committeeRepository';
import { getContacts } from 'apis/repositories/contactsRepository';
import CommitteeMemberCard from '../components/CommitteeMemberCard';
import type { CommitteeAssignment, FamilyContact } from 'types/domain';
import { resolveContactImageSrc } from 'utils/image';

import styles from './Committee.module.css';

const Committee = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contacts, setContacts] = useState<FamilyContact[]>([]);
  const [assignments, setAssignments] = useState<CommitteeAssignment[]>([]);

  useEffect(() => {
    const loadCommittee = async () => {
      setLoading(true);
      setError('');

      try {
        const [contactsResponse, assignmentsResponse] = await Promise.all([
          getContacts(),
          getCommitteeAssignments(),
        ]);
        setContacts(contactsResponse);
        setAssignments(assignmentsResponse);
      } catch (requestError) {
        console.error(requestError);
        setError('Unable to load committee members right now.');
      } finally {
        setLoading(false);
      }
    };

    loadCommittee();
  }, []);

  const assignmentCards = useMemo(() => {
    return assignments
      .map((assignment) => {
        const contact = contacts.find((item) => Number(item.id) === Number(assignment.contactId));
        const member = contact?.familyDetails?.find(
          (item) => Number(item.id) === Number(assignment.memberId)
        );

        if (!member) {
          return null;
        }

        return {
          id: assignment.id,
          name: member.name,
          post: assignment.role,
          relation: member.relation,
          photo: resolveContactImageSrc(member.photoFile),
        };
      })
      .filter(Boolean);
  }, [assignments, contacts]);

  return (
    <div className={styles.container}>
      <div className={styles.heading}>Meet our Committee Members</div>

      {loading ? (
        <div className={styles.loaderWrapper}>
          <CircularProgress size={24} />
        </div>
      ) : null}

      {!loading && error ? <div className={styles.error}>{error}</div> : null}

      {!loading && !error && assignmentCards.length === 0 ? (
        <div className={styles.emptyState}>No committee members assigned yet.</div>
      ) : null}

      {!loading && !error
        ? assignmentCards.map((member) => (
            <CommitteeMemberCard key={member?.id} member={member} />
          ))
        : null}
    </div>
  );
};

export default Committee;